/**
 * Hook reutilizável para paginação server-side + sorting.
 *
 * Equivalente Angular: Service com BehaviorSubject<PageParams> + switchMap + shareReplay
 *
 * Uso:
 *   const { data, isLoading, pagination, sorting } = usePaginatedQuery({
 *     queryKey: [...queryKeys.documentos.artefatos],
 *     fetchFn: (params) => fetchArtefatos({ ...filters, ...params }),
 *     defaultSort: { orderBy: 'created_at', orderDir: 'desc' },
 *   });
 */

import { useCallback, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PaginatedResponse, PaginationParams, SortDirection } from '@/api/client';
import type { QueryPersistPolicy } from '@/lib/pwa/queryPersistence';

export interface SortState {
  orderBy: string;
  orderDir: SortDirection;
}

export interface UsePaginatedQueryOptions<T> {
  queryKey: unknown[];
  fetchFn: (params: PaginationParams) => Promise<PaginatedResponse<T>>;
  defaultSort?: SortState;
  defaultPageSize?: number;
  enabled?: boolean;
  /** Sobrepõe o staleTime global (5min). Ex.: listas cujo status evolui no
   *  servidor sem ação do usuário e querem refetch mais frequente. */
  staleTime?: number;
  meta?: { persist?: QueryPersistPolicy; showToastOnError?: boolean } & Record<string, unknown>;
}

export function usePaginatedQuery<T>({
  queryKey,
  fetchFn,
  defaultSort = { orderBy: 'created_at', orderDir: 'desc' },
  defaultPageSize = 20,
  enabled = true,
  staleTime,
  meta,
}: UsePaginatedQueryOptions<T>) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sorting, setSorting] = useState<SortState>(defaultSort);

  const params: PaginationParams = {
    page,
    page_size: pageSize,
    order_by: sorting.orderBy,
    order_dir: sorting.orderDir,
  };

  const query = useQuery({
    queryKey: [...queryKey, params],
    queryFn: () => fetchFn(params),
    placeholderData: keepPreviousData,
    enabled,
    staleTime,
    meta,
  });

  const goToPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const changePageSize = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  const toggleSort = useCallback((field: string) => {
    setSorting((prev) => {
      if (prev.orderBy === field) {
        return { orderBy: field, orderDir: prev.orderDir === 'asc' ? 'desc' : 'asc' };
      }
      return { orderBy: field, orderDir: 'asc' };
    });
    setPage(1);
  }, []);

  const resetPage = useCallback(() => {
    setPage(1);
  }, []);

  return {
    ...query,
    pagination: {
      page,
      pageSize,
      total: query.data?.total ?? 0,
      pages: query.data?.pages ?? 0,
      goToPage,
      changePageSize,
      resetPage,
    },
    sorting: {
      ...sorting,
      toggleSort,
    },
  };
}
